'use client';
'use no memo';

import * as React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog as MUIDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { useResponsive } from '../../utils';

const RESPONSIVE_CONTENT_SX: SxProps<Theme> = {
  px: { xs: 2, sm: 3 },
  py: { xs: 1, sm: 2 },
};

const RESPONSIVE_ACTIONS_SX: SxProps<Theme> = {
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: 'stretch',
  justifyContent: { xs: 'stretch', sm: 'flex-end' },
  gap: { xs: 1.5, sm: 1 },
  px: { xs: 2, sm: 3 },
  pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 2 },
  pt: { xs: 1, sm: 0 },
  '& > *': {
    margin: '0 !important',
    width: { xs: '100%', sm: 'auto' },
    minHeight: { xs: 44, sm: undefined },
  },
};

export type DialogProps = {
  open: boolean;
  onDialogClose?: () => void;
  title: React.ReactNode;
  content: React.ReactNode;
  actions?: React.ReactNode[];
  width?: string | number;
  height?: string | number;
  minWidth?: string | number;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  disablePortal?: boolean;
  showCloseIcon?: boolean;
  actionsSx?: SxProps<Theme>;
  /**
   * When `undefined`, fullscreen is applied on small viewports (`useResponsive().isSmallTabletDown`).
   * Pass `false` for a windowed dialog on all sizes, or `true` to always use fullscreen.
   */
  fullScreen?: boolean;
  /**
   * When false (default), merges responsive padding into content and actions when present.
   * Set true to opt out (e.g. full-bleed content or fully custom spacing).
   */
  skipResponsiveDefaults?: boolean;
  contentSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
};

export function Dialog({
  title,
  content,
  actions,
  onDialogClose,
  open,
  width,
  height,
  minWidth,
  maxWidth = 'md',
  disablePortal,
  actionsSx,
  showCloseIcon = true,
  fullScreen,
  skipResponsiveDefaults = false,
  contentSx,
  sx,
}: DialogProps): React.ReactElement {
  const { isSmallTabletDown } = useResponsive();

  const effectiveFullScreen =
    fullScreen === undefined ? isSmallTabletDown : fullScreen;

  const mergedPaperSx: SxProps<Theme> = {
    width: effectiveFullScreen ? '100%' : width || 'auto',
    minWidth: effectiveFullScreen ? '100%' : minWidth || 'auto',
    maxWidth: effectiveFullScreen ? '100%' : undefined,
    height: effectiveFullScreen ? '100%' : height || 'auto',
    ...(effectiveFullScreen && {
      margin: 0,
      borderRadius: 0,
      pt: 'env(safe-area-inset-top, 0px)',
    }),
    ...sx,
  };

  const mergedActionsSx: SxProps<Theme> | undefined =
    !actions?.length || skipResponsiveDefaults
      ? actionsSx
      : ([RESPONSIVE_ACTIONS_SX, actionsSx] as SxProps<Theme>);

  const dialogContentSx = [
    effectiveFullScreen && {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
    !skipResponsiveDefaults ? RESPONSIVE_CONTENT_SX : null,
    contentSx,
  ].filter(Boolean) as SxProps<Theme>;

  return (
    <MUIDialog
      onClose={onDialogClose}
      open={open}
      maxWidth={maxWidth}
      fullScreen={effectiveFullScreen}
      disablePortal={disablePortal}
      slotProps={{
        paper: {
          sx: mergedPaperSx,
        },
      }}
    >
      <DialogTitle sx={{ paddingRight: 2, position: 'relative' }}>
        {title}
        {showCloseIcon && (
          <IconButton
            aria-label="close"
            onClick={onDialogClose}
            sx={{ position: 'absolute', top: 0, right: 0 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>{content}</DialogContent>
      {!!actions && (
        <DialogActions sx={mergedActionsSx}>{actions}</DialogActions>
      )}
    </MUIDialog>
  );
}
